import axios from 'axios';

// Define supported platform types
export type Platform = 'x';

// Define quality options
export type VideoQuality = 'high' | 'medium' | 'low';

// Define tweet info interface
interface TweetInfo {
  username: string;
  id: string;
}

// Define FXTwitter API response types
interface FXTwitterVideoVariant {
  url: string;
  bitrate?: number;
}

interface FXTwitterVideo {
  url?: string;
  thumbnail_url?: string;
  preview_image_url?: string;
  duration?: number;
  width?: number;
  height?: number;
  variants?: FXTwitterVideoVariant[];
}

interface FXTwitterPhoto {
  url: string;
  width?: number;
  height?: number;
}

interface FXTwitterMedia {
  videos?: FXTwitterVideo[];
  photos?: FXTwitterPhoto[];
}

interface FXTwitterAuthor {
  name?: string;
}

interface FXTwitterTweet {
  text: string;
  media?: FXTwitterMedia;
  author?: FXTwitterAuthor;
}

interface FXTwitterResponse {
  code: number;
  message: string;
  tweet: FXTwitterTweet;
}

// Video metadata interface
export interface VideoMetadata {
  title: string;
  thumbnail: string;
  author?: string;
  platform: Platform;
  availableQualities: VideoQuality[];
  tweetId?: string;
  url: string;
  videos: Array<{
    url: string;
    thumbnail: string;
    duration?: number;
    width?: number;
    height?: number;
    variants?: Array<{
      url: string;
      bitrate?: number;
    }>;
    playerUrl: string;
  }>;
  images?: Array<{
    url: string;
    width?: number;
    height?: number;
  }>;
  selectedVideoIndex: number;
}

// Function to extract tweet ID from X/Twitter URL
const getXTweetId = (url: string): TweetInfo | null => {
  console.log('Processing URL:', url);
  // Handle both old twitter.com and new x.com URLs
  const regExp = /(?:twitter\.com|x\.com)\/(\w+)\/status\/(\d+)/;
  const match = url.match(regExp);
  console.log('URL match result:', match);
  return match ? { username: match[1], id: match[2] } : null;
};

// Function to get video info from X/Twitter
const getXVideoInfo = async (url: string): Promise<VideoMetadata> => {
  const tweetInfo = getXTweetId(url);
  if (!tweetInfo) throw new Error('Invalid X/Twitter URL');

  try {
    // Use fxtwitter API which allows CORS
    const response = await axios.get<FXTwitterResponse>(
      `https://api.fxtwitter.com/${tweetInfo.username}/status/${tweetInfo.id}`
    );

    if (!response.data?.tweet) {
      throw new Error('Failed to fetch tweet data');
    }

    const tweet = response.data.tweet;

    // Check for media content
    if (!tweet.media) {
      throw new Error('No media found in this X/Twitter post');
    }

    // Get all videos and photos from the tweet
    const videos = tweet.media.videos || [];
    const photos = tweet.media.photos || [];

    if (videos.length === 0 && photos.length === 0) {
      throw new Error('No videos or images found in this X/Twitter post');
    }

    // Process all videos
    const processedVideos = videos.map((video: FXTwitterVideo) => ({
      url: video.url || '',
      thumbnail: video.thumbnail_url || video.preview_image_url || '',
      duration: video.duration,
      width: video.width,
      height: video.height,
      variants: video.variants || [{ url: video.url || '' }],
      // Add a mobile-friendly player URL
      playerUrl: `https://fxtwitter.com/${tweetInfo.username}/status/${tweetInfo.id}/video/${videos.indexOf(video)}`
    }));

    // Process all photos
    const processedPhotos = photos.map((photo: FXTwitterPhoto) => ({
      url: photo.url || '',
      width: photo.width,
      height: photo.height
    }));

    return {
      title: tweet.text || 'X Media',
      thumbnail: processedVideos[0]?.thumbnail || photos[0]?.url || '',
      author: tweet.author?.name || tweetInfo.username,
      platform: 'x',
      availableQualities: ['high', 'medium', 'low'],
      tweetId: tweetInfo.id,
      url: url,
      videos: processedVideos,
      images: processedPhotos,
      selectedVideoIndex: 0
    };
  } catch (error) {
    console.error('Error fetching tweet:', error);
    throw error;
  }
};

// Function to get video info from supported platforms
const getVideoInfo = async (url: string): Promise<VideoMetadata> => {
  if (url.includes('twitter.com') || url.includes('x.com')) {
    return getXVideoInfo(url);
  }
  
  throw new Error('Only X/Twitter videos are supported');
};

// Function to extract quality from video URL
const getQualityFromUrl = (url: string): number => {
  try {
    // Extract quality from URL parameters
    const urlObj = new URL(url);
    const tag = urlObj.searchParams.get('tag');
    if (tag) {
      // Common Twitter video quality tags: 240p, 360p, 480p, 720p, etc.
      const resolution = parseInt(tag.replace(/[^0-9]/g, ''));
      return !isNaN(resolution) ? resolution : 0;
    }
    
    // If no tag parameter, try to extract from the URL path
    const match = url.match(/\/(\d+)x(\d+)\//);
    if (match) {
      return parseInt(match[2]); // Use height as quality indicator
    }
    
    // If no quality info found, return 0
    return 0;
  } catch (error) {
    console.warn('Error parsing video URL:', error);
    return 0;
  }
};

// Function to check if URL is an m3u8 playlist
const isM3U8 = (url: string): boolean => {
  return url.includes('.m3u8') || url.endsWith('m3u8');
};

// Function to get direct MP4 URL from variants
const getDirectMp4Url = (variants: FXTwitterVideoVariant[]): string | null => {
  // Filter for MP4 URLs and sort by bitrate
  const mp4Variants = variants
    .filter(v => v.url && v.url.includes('.mp4'))
    .sort((a, b) => (b.bitrate || 0) - (a.bitrate || 0));

  return mp4Variants.length > 0 ? mp4Variants[0].url : null;
};

// Remove unused functions
const sanitizeFilename = (text: string): string => {
  return text
    .slice(0, 15)
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .toLowerCase();
};

const downloadVideo = async (
  url: string,
  quality: VideoQuality,
  downloadPath: string,
  selectedVideoIndex: number = 0,
  customFilename: string = ''
): Promise<{ success: boolean; filePath: string }> => {
  try {
    const tweetInfo = getXTweetId(url);
    if (!tweetInfo) {
      throw new Error('Invalid X/Twitter URL');
    }

    const mediaInfo = await getXVideoInfo(url);
    if (!mediaInfo) {
      throw new Error('No media found in this X/Twitter post');
    }

    // Determine if we're downloading a video or image based on the selectedVideoIndex
    const isVideo = selectedVideoIndex < mediaInfo.videos.length;
    let downloadUrl: string;
    let mimeType: string;
    let extension: string;

    if (isVideo) {
      // Handle video download
      const video = mediaInfo.videos[selectedVideoIndex];
      if (!video) {
        throw new Error('Selected video not found');
      }

      const variants = video.variants || [{ url: video.url }];
      // Sort variants by quality (bitrate or resolution)
      const mp4Variants = variants
        .filter(v => v.url && v.url.includes('.mp4'))
        .sort((a, b) => {
          // If we have bitrates, use them
          if (a.bitrate && b.bitrate) {
            return (b.bitrate || 0) - (a.bitrate || 0);
          }
          // Otherwise try to extract quality from URL
          const getQuality = (url: string) => {
            const match = url.match(/\/(\d+)x(\d+)\//);
            return match ? parseInt(match[2]) : 0;
          };
          return getQuality(b.url || '') - getQuality(a.url || '');
        });

      if (mp4Variants.length === 0) {
        throw new Error('No MP4 video URL found');
      }

      // Select variant based on quality preference
      let selectedVariant;
      switch (quality) {
        case 'high':
          selectedVariant = mp4Variants[0]; // First is highest quality
          break;
        case 'medium':
          selectedVariant = mp4Variants[Math.floor(mp4Variants.length / 2)];
          break;
        case 'low':
          selectedVariant = mp4Variants[mp4Variants.length - 1];
          break;
        default:
          selectedVariant = mp4Variants[0];
      }

      downloadUrl = selectedVariant.url;
      mimeType = 'video/mp4';
      extension = 'mp4';
    } else {
      // Handle image download
      const imageIndex = selectedVideoIndex - mediaInfo.videos.length;
      if (!mediaInfo.images || !mediaInfo.images[imageIndex]) {
        throw new Error('Selected image not found');
      }
      downloadUrl = mediaInfo.images[imageIndex].url;
      mimeType = 'image/jpeg';
      extension = 'jpg';
    }

    // Generate filename
    let filename: string;
    if (customFilename.trim()) {
      // Use custom filename if provided, sanitize it
      const sanitizedCustomName = customFilename
        .trim()
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_+/g, '_')
        .toLowerCase();
      filename = `${sanitizedCustomName}.${extension}`;
    } else {
      // Generate default filename using first 15 characters of tweet
      const shortTitle = mediaInfo.title
        .substring(0, 15)
        .trim()
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_+/g, '_')
        .toLowerCase();

      // Add media type and number suffix if there are multiple media items
      const totalMediaCount = (mediaInfo.videos || []).length + (mediaInfo.images || []).length;
      let mediaSuffix = '';
      
      if (totalMediaCount > 1) {
        if (isVideo) {
          mediaSuffix = `_vid_${selectedVideoIndex + 1}`;
        } else {
          mediaSuffix = `_img_${(selectedVideoIndex - mediaInfo.videos.length) + 1}`;
        }
      }

      filename = `${tweetInfo.username}_${shortTitle}${mediaSuffix}.${extension}`;
    }

    // Fetch the media
    const response = await fetch(downloadUrl, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
        'Accept': '*/*',
        'Referer': 'https://twitter.com/',
        'Origin': 'https://twitter.com'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to download media: ${response.status}`);
    }
    
    const blob = await response.blob();
    if (blob.size < 100) { // Reduced minimum size check for images
      throw new Error(`Downloaded file is too small (${blob.size} bytes)`);
    }

    // Create download link
    const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);

    return { success: true, filePath: filename };
  } catch (error) {
    console.error('Error downloading media:', error);
    throw error;
  }
};

export const videoService = {
  getVideoInfo,
  downloadVideo
}; 